import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2, XCircle } from 'lucide-react'

import PasswordInput from '@/components/auth/PasswordInput'
import { Button } from '@/components/ui/button'
import { useCreatePasswordMutation } from '@/hooks/useAuthActions'

const CreatePassword = () => {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const createPassword = useCreatePasswordMutation()
    const token = searchParams.get('token')

    useEffect(() => {
        if (!token) {
            navigate('/auth/forgot-password', { replace: true })
        }
    }, [token, navigate])

    const requirements = useMemo(
        () => [
            {
                id: 'uppercase',
                label: 'At least 1 uppercase',
                met: /[A-Z]/.test(password),
            },
            {
                id: 'number',
                label: 'At least 1 number',
                met: /\d/.test(password),
            },
            {
                id: 'length',
                label: 'At least 8 characters',
                met: password.length >= 8,
            },
        ],
        [password]
    )

    const metCount = requirements.filter(req => req.met).length
    const strengthClass =
        metCount <= 1
            ? 'bg-[#EB4B4B]'
            : metCount === 2
              ? 'bg-[#F4B740]'
              : 'bg-[#0FA3B1]'
    const passwordsMatch =
        confirmPassword.length === 0 ? true : confirmPassword === password
    const canSubmit =
        requirements.every(req => req.met) &&
        passwordsMatch &&
        password.length > 0 &&
        confirmPassword.length > 0

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!canSubmit || !token) return
        createPassword.mutate({
            token,
            newPassword: password,
        })
    }

    if (!token) {
        return null
    }

    return (
        <div className="space-y-8">
            <div className="space-y-2">
                <h1 className="text-3xl font-semibold text-[#0C1324]">
                    Create new password
                </h1>
                <p className="text-sm text-[#6F7785]">
                    Create a new password to secure your Ogaboss account.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-4">
                    <div>
                        <PasswordInput
                            placeholder="Enter password"
                            value={password}
                            className="rounded-[12px]"
                            onChange={event => setPassword(event.target.value)}
                            required
                        />
                        <div className="mt-4">
                            <div className="flex gap-2">
                                {[0, 1, 2].map(segment => (
                                    <span
                                        key={segment}
                                        className={`h-1 flex-1 rounded-full ${
                                            segment < metCount
                                                ? strengthClass
                                                : 'bg-[#E6E9F2]'
                                        }`}
                                    />
                                ))}
                            </div>
                            <p className="mt-3 text-sm font-medium text-[#0C1324]">
                                Must contain at least;
                            </p>
                            <ul className="mt-2 space-y-2 text-sm text-[#6F7785]">
                                {requirements.map(requirement => (
                                    <li
                                        key={requirement.id}
                                        className="flex items-center gap-2"
                                    >
                                        {requirement.met ? (
                                            <CheckCircle2 className="h-4 w-4 text-[#0CB477]" />
                                        ) : (
                                            <XCircle className="h-4 w-4 text-[#A2AAB8]" />
                                        )}
                                        <span>{requirement.label}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <PasswordInput
                        placeholder="Confirm password"
                        value={confirmPassword}
                        className="rounded-[12px]"
                        onChange={event =>
                            setConfirmPassword(event.target.value)
                        }
                        required
                        aria-invalid={!passwordsMatch}
                    />
                    {!passwordsMatch ? (
                        <p className="text-sm text-destructive">
                            Passwords do not match.
                        </p>
                    ) : null}
                </div>

                {createPassword.isError ? (
                    <p className="text-sm text-destructive">
                        {(createPassword.error as Error)?.message ??
                            'Unable to update password.'}
                    </p>
                ) : null}

                <Button
                    type="submit"
                    disabled={createPassword.isPending || !canSubmit}
                    className="h-12 w-full rounded-full bg-[#0FA3B1] text-base font-semibold hover:bg-[#0C8D98] disabled:bg-[#9EDBE1] disabled:text-white"
                >
                    {createPassword.isPending ? 'Saving...' : 'Create password'}
                </Button>
            </form>
        </div>
    )
}

export default CreatePassword
